import { useState } from "react";
import { Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import LogoTile from "@/components/app/LogoTile";
import AuthLayout from "@/components/app/AuthLayout";
import PasswordInput from "@/components/app/PasswordInput";

const LoginPage = () => {
    const { user, login, signup } = useAuth();
    const nav = useNavigate();
    const location = useLocation();
    const banner = location.state?.banner;
    const [mode, setMode] = useState("login");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [signupSuccess, setSignupSuccess] = useState(false);

    if (user && user !== false) return <Navigate to="/reports" replace />;

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setBusy(true);
        try {
            if (mode === "login") {
                await login(email, password);
                nav("/reports");
            } else {
                await signup(email, password);
                setSignupSuccess(true);
            }
        } catch (err) {
            const s = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (s === 429 && detail?.error === "TOO_MANY_ATTEMPTS") {
                setError(`Too many attempts. Try again in ${detail.retry_after_seconds}s.`);
            } else if (s === 403 && detail?.error === "ACCOUNT_PENDING") {
                setError(detail.message || "Your account is awaiting admin approval.");
            } else if (s === 403 && detail?.error === "ACCOUNT_REJECTED") {
                setError(detail.message || "Your account request was declined. Contact support.");
            } else {
                setError(formatApiError(err));
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <AuthLayout testId="login-page">
            <div className="min-h-screen w-full flex items-center justify-center px-4 py-10 route-fade">
            <div className="w-full max-w-[440px]">
                <div className="flex flex-col items-center mb-8">
                    <LogoTile size={72} className="mb-5" testId="login-logo" />
                    <h1 className="text-3xl font-semibold text-slate-900 text-center">
                        ImpressSite Pro
                    </h1>
                    <p className="text-[14px] text-slate-600 mt-2 text-center">
                        Record and certify the day's crew activity from the site.
                    </p>
                </div>

                <div className="glass-card">
                    {signupSuccess ? (
                        <div className="flex flex-col items-center text-center gap-3 py-6" data-testid="signup-success-card">
                            <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                                <CheckCircle2 size={28} strokeWidth={2} />
                            </div>
                            <div className="text-[17px] font-semibold text-slate-900">Request received.</div>
                            <p className="text-[14px] text-slate-600 leading-relaxed max-w-[320px]">
                                An admin will review your account and email you when it's approved.
                                You can close this tab.
                            </p>
                            <button
                                type="button"
                                onClick={() => { setSignupSuccess(false); setMode("login"); setEmail(""); setPassword(""); }}
                                className="btn btn-secondary btn-sm mt-2"
                                data-testid="signup-back-to-login"
                            >
                                Back to log in
                            </button>
                        </div>
                    ) : (
                    <>
                    {banner && (
                        <div
                            className="mb-5 rounded-[10px] border border-emerald-200 bg-emerald-50/70 text-emerald-800 text-[13px] px-3 py-2 flex items-start gap-2"
                            data-testid="login-banner"
                        >
                            <CheckCircle2 size={16} strokeWidth={2} className="mt-0.5 shrink-0" />
                            <span>{banner}</span>
                        </div>
                    )}
                    <div className="flex justify-center mb-6">
                        <div className="tab-pill-group" data-testid="login-tabs">
                            <button
                                type="button"
                                className="tab-pill"
                                data-active={mode === "login"}
                                onClick={() => setMode("login")}
                                data-testid="login-tab-login"
                            >
                                Log in
                            </button>
                            <button
                                type="button"
                                className="tab-pill"
                                data-active={mode === "signup"}
                                onClick={() => setMode("signup")}
                                data-testid="login-tab-signup"
                            >
                                Sign up
                            </button>
                        </div>
                    </div>

                    <form onSubmit={submit} className="flex flex-col gap-4" data-testid="login-form">
                        <div>
                            <label className="label label-required" htmlFor="login-email">
                                Email
                            </label>
                            <input
                                id="login-email"
                                type="email"
                                autoComplete="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="input"
                                data-testid="login-email-input"
                            />
                        </div>
                        <div>
                            <label className="label label-required" htmlFor="login-password">
                                Password
                            </label>
                            <PasswordInput
                                id="login-password"
                                autoComplete={mode === "login" ? "current-password" : "new-password"}
                                minLength={mode === "signup" ? 8 : 1}
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid="login-password-input"
                            />
                            {mode === "signup" && (
                                <div className="hint">Minimum 8 characters.</div>
                            )}
                            {mode === "login" && (
                                <div className="flex justify-end mt-1">
                                    <Link
                                        to="/forgot-password"
                                        className="text-[12px] text-emerald-700 hover:text-emerald-800"
                                        data-testid="login-forgot-link"
                                    >
                                        Forgot password?
                                    </Link>
                                </div>
                            )}
                        </div>

                        {error && (
                            <div
                                className="err rounded-[10px] border border-red-200 bg-red-50/70 px-3 py-2"
                                data-testid="login-error"
                            >
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            className="btn btn-primary w-full py-3 mt-2"
                            disabled={busy}
                            data-testid="login-submit-button"
                        >
                            {busy ? "…" : mode === "login" ? "Log in" : "Create account"}
                            {!busy && <ArrowRight size={16} strokeWidth={2} />}
                        </button>
                    </form>
                    </>
                    )}
                </div>

                <p className="text-center text-[12px] text-slate-500 mt-6">
                    Email &amp; password only. No SSO yet.
                </p>
            </div>
        </div>
        </AuthLayout>
    );
};

export default LoginPage;
